import { FC, useEffect, useState, useContext, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from 'react-router-dom';
import i18n from 'i18next';
import { Tab } from "@headlessui/react";
import Input from "shared/Input/Input";
import Label from "components/Label/Label";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import ButtonSecondary from "shared/Button/ButtonSecondary";
import "./CustomerInfoPage.scss";
import { PaymentOptions } from "./constant";
import Select from "shared/Select/Select";
import { getPureArray, safeParse, checkBrowser, isValidPhoneNumber, getKeyByLang } from "utils/util";
import { CartCacheLocalStorageKey, BuyFormInfoCacheLocalStorageKey } from "utils/constant";
import { commonAxiosPostRequest } from "utils/request";
import OrderReview from "./components/OrderReview";
import ShippingAddress from "./components/ShippingAddress";
import PaymentMethodTab from "./components/PaymentMethodTab";
import PaymeQrCodeRender from "./components/PaymeQrCodeRender";
import CouponCardRender from "./components/CouponCardRender";
import CommonDialog from "../../components/Dialog/index";
import { AuthContext } from "context/authenticate";
import { Redirect } from "react-router-dom";
import Spinner from "components/Spinner/Spinner";
import { cartToItems, trackAddPaymentInfo, trackAddShippingInfo, trackBeginCheckout } from "utils/analytics";

export interface CustomerInfoProps {
	className?: string;
}

type TPaymentForm = {
	/* 聯絡人姓氏 */
	customerFirstName: string;
	/* 聯絡人名字 */
	customerLastName: string;
	/* 聯絡人手机号 */
	customerPhone: string;
	/* 聯絡人邮箱 */
	customerEmail: string;
	/* 聯絡人收货信息 */
	regionInfo: {
		province: string;
		city: string;
		area?: string;
		address: string;
	},
	/* 支付方式 */
	paymentMethod: string;
	/* 渠道 */
	chInfoText: string;
	/* 渠道-其他 */
	chInfoTextOther: string;
	/* 优惠券 */
	couponCode: string;
}

type TFormValidData = {
	[key in keyof TPaymentForm]?: string;
};

const CustomerInfoPage: FC<CustomerInfoProps> = ({ className = "" }) => {
	const { userInfo } = useContext(AuthContext);
	const { t } = useTranslation();
	const history = useHistory();
	const { language } = i18n;
	// 购物车商品列表
	const [cartSkuList, setCartSkuList] = useState<any[]>([]);
	// 总价(付款金额)
	const [transcationTotalAmount, setTranscationTotalAmount] = useState<number>(0);
	// 订单原始金额
	const [originCartTransactionTotal, setOriginCartTransactionTotal] = useState<number>(0);
	// 优惠券抵扣金额
	const [couponDiscountAmount, setCouponDiscountAmount] = useState<number>(0);
	// VIP折扣金额
	const [vipDiscountAmount, setVipDiscountAmount] = useState<number>(0);
	// 运费信息
	const [shippingFeeInfo, setShippingFeeInfo] = useState<{ expressFee?: number; originExpressFee?: number }>({});
	// 默认使用的折扣信息
	const [discountInfo, setDiscountInfo] = useState<any>(null);
	/* 表单 */
	const [values, setValues] = useState<TPaymentForm>({
		customerFirstName: "",
		customerLastName: "",
		customerPhone: "",
		customerEmail: "",
		couponCode: "",
		regionInfo: {
			province: '',
			city: '',
			address: '',
		},
		paymentMethod: "CreditCard",
		chInfoText: "",
		chInfoTextOther: "",
	});
	const [formValid, setFormValid] = useState<TFormValidData>({});
	// 弹框展示信息
	const [dialogInfo, setDialogInfo] = useState({
		show: false,
		title: '',
		content: '',
	});
	/* PayMe支付订单信息 */
	const [paymeOrderInfo, setPaymeOrderInfo] = useState({
		qrLink: "",
		orderId: "",
	});
	// 是否为移动端浏览器
	const [isMobile, setIsMobile] = useState(checkBrowser().isMobile);
	// 页面加载状态：0/1/2
	const [loadingState, setLoadingState] = useState<0 | 1>(0);
	const [cartLoading, setCartLoading] = useState(true);
	const orderAmountRequestIdRef = useRef(0);
	const beginCheckoutTrackedRef = useRef(false);
	const shippingInfoTrackedRef = useRef(false);
	const BuyChInfoList = [
		{ label: t('common.select_placeholder'), value: "" },
		{ label: "Facebook / Instagram", value: "Facebook / Instagram" },
		{ label: t('checkout.exhibition'), value: t('checkout.exhibition') },
		{ label: t('checkout.old_customers'), value: t('checkout.old_customers') },
		{ label: "HKTVMALL", value: "HKTVMALL" },
		{ label: t('checkout.search_online'), value: t('checkout.search_online') },
		{ label: t('checkout.introduced_by_friends'), value: t('checkout.introduced_by_friends') },
		{ label: t('checkout.other'), value: t('checkout.other') },
	];


	// 输入框输入change
	const onInputChange = (e: any) => {
		const { name, value } = e.target;
		setValues((prevState) => ({ ...prevState, [name]: value }));
	};

	// Select选择change
	const onSelectChange = (e: any) => {
		const { name, value } = e.target;
		setValues((values) => ({ ...values, [name]: value }));
	};

	// 修改收货地址change
	const onRegionInfoChange = (newRegionInfo: TPaymentForm['regionInfo'], type: string) => {
		setValues((prevState) => ({ ...prevState, regionInfo: newRegionInfo }));
	}

	// 创建订单
	const onSubmitButtonClick = async () => {
		// console.log('[onSubmitButtonClick]', values);
		window.localStorage.setItem(BuyFormInfoCacheLocalStorageKey, JSON.stringify(values));
		if (loadingState === 1) {
			return;
		}
		const __formValid: TFormValidData = {
			customerFirstName: !values.customerFirstName ? t('checkout.first_name_input_error') : '',
			customerLastName: !values.customerLastName ? t('checkout.last_name_input_error') : '',
			customerPhone: !isValidPhoneNumber(values.customerPhone) ? t('checkout.phone_input_error') : '',
			regionInfo: (!values.regionInfo.province || !values.regionInfo.city || !values.regionInfo.address) ? t('checkout.address_input_error') : '',
		};
		setFormValid(__formValid);
		if (Object.keys(__formValid).filter((key) => __formValid[key as keyof TFormValidData]).length > 0) {
			setDialogInfo((prev) => ({ ...prev, show: true, title: t('checkout.form_input_error'), content: Object.values(__formValid).filter(n => n)[0] }));
			return;
		}
		setLoadingState(1);
		const [error, response] = await commonAxiosPostRequest<any, {}>('/universal_mall/api/app/product/createOrder', {
			...values,
			skuList: cartSkuList.reduce((p, v) => {
				const list = v.skuList.map((n: any) => ({ productId: v.productId, id: n.id, quantity: n.quantity })).filter((n: any) => n.quantity > 0);
				return [...p, ...list];
			}, []),
			customerRegionProvinceCode: values.regionInfo.province,
			customerRegionCityCode: values.regionInfo.city,
			customerAddress: values.regionInfo?.address || '',
			chInfoText: values.chInfoText === t('checkout.other') ? values.chInfoTextOther : values.chInfoText,
			customerPostalCode: "999077",
		});
		setLoadingState(0);
		if (!error && response?.orderId) {
			const { orderId, paymentMethod, data } = response;
			trackAddPaymentInfo(cartToItems(cartSkuList), {
				value: transcationTotalAmount > 0 ? transcationTotalAmount : undefined,
				coupon: values.couponCode,
				payment_type: paymentMethod,
			});
			if (paymentMethod === 'COD') {
				history.push(`/payment-result?orderId=${orderId}`);
			} else if (paymentMethod === 'PayMe') {
				const { webLink } = data;
				// Payme支付方式则是：PC端生成支付二维码、Mobile则跳转去支付链接
				if (isMobile) {
					window.location.replace(webLink);
				} else {
					setPaymeOrderInfo({ qrLink: webLink, orderId });
				}
			} else {
				const formHtml = data?.formHtml;
				const paymentFormBoxDom = document.querySelector("#payment-form");
				if (!formHtml || !paymentFormBoxDom) {
					setDialogInfo((prev) => ({ ...prev, show: true, title: '訂單建立失敗', content: '支付表單載入失敗，請稍後再試' }));
					return;
				}
				paymentFormBoxDom.innerHTML = formHtml;
				setTimeout(() => {
					const paymentFormDom = document.querySelector("#payment-form form") as HTMLFormElement | null;
					if (!paymentFormDom) {
						setDialogInfo((prev) => ({ ...prev, show: true, title: '訂單建立失敗', content: '支付表單載入失敗，請稍後再試' }));
						return;
					}
					paymentFormDom.submit();
				}, 0);
			}
		} else {
			setDialogInfo((prev) => ({ ...prev, show: true, title: '訂單建立失敗', content: error?.message || '系統繁忙，請稍後再試' }));
		}
	}

	// 初始化获取缓存中的购物车商品sku列表
	const handleGetCartSkuList = async () => {
		let cachedCartSkuList: any[] = safeParse(window.localStorage.getItem(CartCacheLocalStorageKey), []);
		cachedCartSkuList = cachedCartSkuList.map(item => ({
			...item,
			skuList: item.skuList.filter((sku: any) => sku.quantity > 0),
		})).filter(item => item.skuList.length > 0);
		const [error, response] = await commonAxiosPostRequest<{ list: any[], transcationTotalAmount: number, shippingFeeText: string }, {}>('/universal_mall/api/app/product/cart', {
			cartSkuList: cachedCartSkuList.map(item => ({
				productId: item.productId,
				skuList: item.skuList.map((sku: any) => ({ id: sku.id, quantity: sku.quantity }))
			}))
		});
		const list = getPureArray<any>(response?.list);
		window.localStorage.setItem(CartCacheLocalStorageKey, JSON.stringify(list));
		setCartSkuList(list);
		setCartLoading(false);
	}

	// 计算订单金额。省份编码由用户资料或地址组件异步填入，未就绪时不请求，避免空参数报错
	const handleGetOrderAmountCalculation = async () => {
		const provinceCode = values.regionInfo?.province;
		if (!provinceCode) {
			return;
		}
		const skuList = cartSkuList.reduce((p, v) => {
			const list = v.skuList.map((n: any) => ({ productId: v.productId, id: n.id, quantity: n.quantity })).filter((n: any) => n.quantity > 0);
			return [...p, ...list];
		}, []);
		if (skuList.length === 0) {
			return;
		}
		const requestId = ++orderAmountRequestIdRef.current;
		const [error, response] = await commonAxiosPostRequest<any, {}>('/universal_mall/api/app/product/orderAmountCalculation', {
			skuList,
			customerRegionProvinceCode: provinceCode,
			couponCode: values?.couponCode || '',
		});
		if (requestId !== orderAmountRequestIdRef.current) {
			return;
		}
		if (error) {
			setDialogInfo((prev) => ({ ...prev, show: true, title: '提示', content: error?.message || '查詢失敗，請稍後重試' }));
			if (error?.code === 'COUPON_INVALID') {
				setCouponDiscountAmount(0);
			}
		} else {
			setTranscationTotalAmount(response?.cartTransactionTotal || 0);
			setOriginCartTransactionTotal(response?.originCartTransactionTotal || 0);
			setShippingFeeInfo({
				expressFee: response?.expressFee || 0,
				originExpressFee: response?.originExpressFee || 0,
			});
			setCouponDiscountAmount(response?.couponDiscountAmount || 0);
			setVipDiscountAmount(response?.vipDiscountAmount || 0);
			setDiscountInfo(response?.discountInfo || null);
			if (!shippingInfoTrackedRef.current) {
				shippingInfoTrackedRef.current = true;
				trackAddShippingInfo(cartToItems(cartSkuList), {
					value: response?.cartTransactionTotal || undefined,
					coupon: values?.couponCode || "",
					shipping_tier: "standard",
				});
			}
		}
	}

	// 初始化表单
	const handleInitForm = async () => {
		const localFormData = safeParse(window.localStorage.getItem(BuyFormInfoCacheLocalStorageKey), {}) || {};
		const [province, city] = (userInfo?.expressRegion || '')?.split('__');
		setValues({
			customerFirstName: userInfo?.expressLastName || localFormData.customerFirstName || "",
			customerLastName: userInfo?.expressFirstName || localFormData.customerLastName || "",
			customerPhone: userInfo?.expressPhone || localFormData.customerPhone || "",
			customerEmail: userInfo?.expressEmail || localFormData.customerEmail || "",
			regionInfo: {
				province: province || localFormData?.regionInfo?.province || "",
				city: city || localFormData?.regionInfo?.city || "",
				address: userInfo?.expressAddress || localFormData?.regionInfo?.address || "",
			},
			paymentMethod: localFormData.paymentMethod || "CreditCard",
			chInfoText: localFormData.chInfoText || "",
			chInfoTextOther: localFormData.chInfoTextOther || "",
			couponCode: "",
		});
	}

	const onCouponCodeInputBlur = () => {
		handleGetOrderAmountCalculation();
	}

	useEffect(() => {
		handleGetCartSkuList();
	}, []);

	useEffect(() => {
		if (cartLoading || beginCheckoutTrackedRef.current || cartSkuList.length === 0) {
			return;
		}
		beginCheckoutTrackedRef.current = true;
		trackBeginCheckout(cartToItems(cartSkuList), { coupon: values.couponCode });
	}, [cartLoading, cartSkuList]);

	useEffect(() => {
		if (userInfo !== undefined) {
			handleInitForm();
		}
	}, [userInfo]);

	useEffect(() => {
		if (Array.isArray(cartSkuList) && cartSkuList.length > 0 && values?.regionInfo?.province) {
			handleGetOrderAmountCalculation();
		}
	}, [cartSkuList, values?.regionInfo?.province]);

	const paymentOptions = userInfo?.userType === 2 ? [...PaymentOptions, { label: "C.O.D", labelEn: 'C.O.D', labelHk: 'C.O.D', value: "COD" }] : PaymentOptions;

	if (cartLoading) {
		return <Spinner className="items-center py-24 lg:py-32" />;
	}
	if (cartSkuList.length === 0) {
		return <Redirect to="/cart" />;
	}

	const renderMain = () => {
		return (
			<>
				<div className="w-full flex flex-col sm:rounded-2xl sm:border border-neutral-200 dark:border-neutral-700 space-y-6 sm:space-y-8 px-0 sm:p-6 xl:p-8">
					<h2 className="text-2xl sm:text-3xl lg:text-4xl font-semibold">{t('checkout.settlement')}</h2>
					<h3 className="text-xl sm:text-2xl font-semibold">{t('checkout.information_input_tip')}</h3>
					<div className="border-b border-neutral-200 dark:border-neutral-700"></div>
					<div>
						<h3 className="text-xl sm:text-2xl font-semibold">{t('checkout.payment_way_tip')}:</h3>
						<div className="mt-4 sm:mt-6">
							<Tab.Group>
								<PaymentMethodTab />
								<h3 className="text-base sm:text-xl font-semibold mt-6">** {t('checkout.phone_input_tip')} **</h3>
								<div className="w-100 border-b border-neutral-200 my-5"></div>
								<Tab.Panels>
									<Tab.Panel className="space-y-5">
										<div className="space-y-1">
											<Label>{t('checkout.last_name')}</Label>
											<Input
												type="text"
												name="customerLastName"
												value={values.customerLastName}
												onChange={onInputChange}
												placeholder={t('checkout.last_name_input_error')}
											/>
										</div>
										<div className="space-y-1">
											<Label>{t('checkout.first_name')}</Label>
											<Input
												type="text"
												name="customerFirstName"
												value={values.customerFirstName}
												onChange={onInputChange}
												placeholder={t('checkout.first_name_input_error')}
											/>
										</div>
										<div className="space-y-1">
											<Label>{t('checkout.email')}</Label>
											<Input
												type="email"
												name="customerEmail"
												value={values.customerEmail}
												onChange={onInputChange}
												placeholder={t('checkout.email_input_error')}
											/>
										</div>
										<div className="space-y-1">
											<Label>{t('checkout.phone')}</Label>
											<Input
												name="customerPhone"
												value={values.customerPhone}
												onChange={onInputChange}
												placeholder={t('checkout.phone_input_error')}
											/>
										</div>
										<div className="border-b border-neutral-200 dark:border-neutral-700 pt-[8px]"></div>
										<div className="space-y-1">
											<Label>{t('checkout.address')}</Label>
											<ShippingAddress regionInfo={values.regionInfo} onChange={onRegionInfoChange}></ShippingAddress>
										</div>
										<div className="space-y-1">
											<Label>{t('checkout.chinfo')}</Label>
											<div className="mt-1.5 flex flex-col gap-2 sm:flex-row">
												<Select className="sm:flex-1" name="chInfoText" onChange={onSelectChange} value={values.chInfoText}>
													{BuyChInfoList.map((item: any) => {
														return (<option value={item.value} key={item.value}>{item.label}</option>);
													})}
												</Select>
												{values.chInfoText === t('checkout.other')
													? <Input className="sm:flex-1" name="chInfoTextOther" value={values.chInfoTextOther} onChange={onInputChange} placeholder={t('checkout.please_note')} />
													: null}
											</div>
										</div>
										<div className="border-b border-neutral-200 dark:border-neutral-700 pt-[8px]"></div>
										<div className="space-y-1">
											<Label>{t('checkout.coupon')}</Label>
											<CouponCardRender></CouponCardRender>
											<Input
												name="couponCode"
												disabled={userInfo?.id ? false : true}
												value={values.couponCode}
												onChange={onInputChange}
												onBlur={onCouponCodeInputBlur}
												placeholder={t('checkout.couponUseWay')}
											/>
										</div>
										<div className="space-y-1">
											<Label>{t('checkout.payment_way')}</Label>
											<Select className="mt-1.5" value={values.paymentMethod} name="paymentMethod" onChange={onSelectChange}>
												{paymentOptions.map((point: any) => {
													return (<option value={point.value} key={point.value}>{point[getKeyByLang(language, 'label')]}</option>);
												})}
											</Select>
										</div>
										<div className="space-y-1">
											<>
												<Label>{t('checkout.submit_step_1')}</Label>
												<br />
												<Label>{t('checkout.submit_step_2')}</Label>
												<br />
												<Label>{t('checkout.submit_step_3')}</Label>
												<br />
											</>
										</div>
										{paymeOrderInfo.qrLink ? <PaymeQrCodeRender {...paymeOrderInfo} /> : null}
									</Tab.Panel>
								</Tab.Panels>
							</Tab.Group>
						</div>
					</div>
					<CommonDialog
						{...dialogInfo}
						onClose={() => setDialogInfo((prev) => ({ ...prev, show: false }))}
					/>
					<div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
						<ButtonSecondary className="w-full sm:w-auto" href={"/cart"}>{t('checkout.back_cart')}</ButtonSecondary>
						<ButtonPrimary type="submit" className="w-full sm:w-auto" loading={loadingState === 1} onClick={onSubmitButtonClick}>{t('checkout.submit')}</ButtonPrimary>
					</div>
				</div>
			</>
		);
	};

	return (
		<main className="container mt-6 mb-24 sm:mt-11 lg:mb-32">
			<div className="flex flex-col gap-8 lg:flex-row lg:items-start lg:gap-10">
				<div className="w-full min-w-0 lg:w-3/5 xl:w-2/3 order-2 lg:order-1">
					{renderMain()}
					<div id="payment-form"></div>
				</div>
				<div className="w-full min-w-0 lg:flex-1 order-1 lg:order-2 lg:sticky lg:top-24">
					<OrderReview
						transcationTotalAmount={transcationTotalAmount}
						originCartTransactionTotal={originCartTransactionTotal}
						shippingFee={shippingFeeInfo.expressFee || 0}
						originExpressFee={shippingFeeInfo.originExpressFee || 0}
						cartSkuList={cartSkuList}
						discountInfo={discountInfo}
						couponDiscountAmount={couponDiscountAmount}
						vipDiscountAmount={vipDiscountAmount}
					/>
				</div>
			</div>
		</main>
	);
};

export default CustomerInfoPage;
